import { ReactNode, useEffect, useRef, useState } from "react";

export const AnimatedCollapse = ({ expanded, children, keepChildren }: { expanded: boolean; children: ReactNode; keepChildren?: boolean }) => {
	const [shouldRender, setShouldRender] = useState(expanded);
	const ref = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const expandedRef = useRef(expanded);
	expandedRef.current = expanded;

	useEffect(() => {
		if (expanded) setShouldRender(true);
	}, [expanded]);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		if (expanded) {
			el.style.overflow = "hidden";
			el.style.transition = "none";
			el.style.height = "0px";
			void el.offsetHeight;
			el.style.transition = "height 0.3s ease";
			el.style.height = el.scrollHeight + "px";
		} else {
			el.style.overflow = "hidden";
			el.style.transition = "none";
			el.style.height = el.scrollHeight + "px";
			void el.offsetHeight;
			el.style.transition = "height 0.3s ease";
			el.style.height = "0px";
		}
	}, [expanded, shouldRender]);

	useEffect(() => {
		const content = contentRef.current;
		if (!content) return;

		let prevHeight = content.offsetHeight;

		const ro = new ResizeObserver(() => {
			if (!ref.current || !contentRef.current || !expandedRef.current) return;
			const newHeight = contentRef.current.offsetHeight;
			if (Math.abs(newHeight - prevHeight) < 1) return;

			const el = ref.current;
			if (el.style.height === "auto") {
				el.style.overflow = "hidden";
				el.style.transition = "none";
				el.style.height = prevHeight + "px";
				void el.offsetHeight;
				el.style.transition = "height 0.3s ease";
				el.style.height = newHeight + "px";
			} else {
				el.style.height = newHeight + "px";
			}

			prevHeight = newHeight;
		});

		ro.observe(content);
		return () => ro.disconnect();
	}, [shouldRender]);

	const handleTransitionEnd = (e: TransitionEvent) => {
		if (e.target !== ref.current) return;
		if (!expanded && !keepChildren) setShouldRender(false);
		if (expanded && ref.current) {
			ref.current.style.height = "auto";
			ref.current.style.overflow = "visible";
		}
	};

	if (!shouldRender) return null;

	return (
		<div
			ref={ref}
			style={{
				overflow: "hidden",
				transition: "height 0.3s ease",
				height: 0,
			}}
			onTransitionEnd={handleTransitionEnd as any}>
			<div ref={contentRef}>{children}</div>
		</div>
	);
};
